import { useEffect, useRef } from 'react';

interface MatrixBackgroundProps {
  color?: string;
  className?: string;
  animated?: boolean;
}

export default function MatrixBackground({ color = '#00ff41', className = '', animated = true }: MatrixBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Función para obtener el color RGB desde hex
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : { r: 0, g: 255, b: 65 };
    };

    const rgb = hexToRgb(color);

    // Ajustar canvas al tamaño del contenedor
    const resizeCanvas = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    };
    resizeCanvas();

    // Caracteres para el efecto Matrix
    const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
    const fontSize = 16;
    const columnSpacing = fontSize * 2; // Espaciado doble = mitad de columnas
    const columns = canvas.width / columnSpacing;
    const drops: number[] = [];

    // Inicializar posiciones de las columnas
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100;
    }

    // Función para dibujo estático (sin animación)
    const drawStatic = () => {
      const currentColumns = canvas.width / columnSpacing;
      ctx.fillStyle = `rgba(0, 0, 0, 0.5)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      // Dibujar caracteres estáticos en posiciones fijas
      for (let i = 0; i < currentColumns; i++) {
        const x = i * columnSpacing;
        // Distribuir caracteres verticalmente de forma estática
        for (let j = 0; j < Math.floor(canvas.height / fontSize); j++) {
          const y = j * fontSize;
          const text = chars[Math.floor(Math.random() * chars.length)];
          
          // Opacidad aleatoria para efecto visual
          const opacity = Math.random() * 0.3 + 0.1;
          ctx.shadowBlur = 5;
          ctx.shadowColor = color;
          ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
          ctx.fillText(text, x, y);
        }
      }
      
      ctx.shadowBlur = 0;
    };

    // Función de animación
    const draw = () => {
      // Fondo semi-transparente para efecto de rastro (más bajo = letras más visibles)
      ctx.fillStyle = `rgba(0, 0, 0, 0.03)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      // Dibujar caracteres
      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        const x = i * columnSpacing;
        const y = drops[i] * fontSize;

        // Efecto de brillo: primer carácter más brillante (blanco)
        ctx.shadowBlur = 15;
        ctx.shadowColor = color;
        ctx.fillStyle = '#ffffff';
        ctx.fillText(text, x, y);
        
        // Caracteres anteriores con color normal
        ctx.shadowBlur = 8;
        ctx.fillStyle = color;
        if (drops[i] > 1) {
          const prevText = chars[Math.floor(Math.random() * chars.length)];
          ctx.fillText(prevText, i * columnSpacing, (drops[i] - 1) * fontSize);
        }

        // Resetear posición cuando llega al final
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        drops[i]++;
      }
      
      // Reset shadow
      ctx.shadowBlur = 0;
    };

    const resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
      if (!animated) {
        drawStatic();
      }
    });
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    if (animated) {
      const interval = setInterval(draw, 42);
      return () => {
        clearInterval(interval);
        resizeObserver.disconnect();
      };
    } else {
      // Dibujar una vez de forma estática
      drawStatic();
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [color, animated]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ imageRendering: 'pixelated' }}
    />
  );
}


